import {onSnapshot, orderBy, query} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import type {CourseId, UserId} from 'types/common';
import {
  getSectionTeachingAttemptsCollectionRef,
  type SectionTeachingAttempt,
} from './sectionTeachingAttempt';

/** Options for {@link useSectionTeachingAttempts}. */
export interface UseSectionTeachingAttemptsOptions {
  /**
   * `true` subscribes to the staff demo collection (`demoTeachAttempts`)
   * instead of the applicant one. Omitted → the real applicant collection.
   */
  demo?: boolean;
}

/**
 * Live view of a Section Leader applicant's section-teaching attempts. Used by
 * the launcher (to show the applicant their own attempt history + let them pick
 * a final one) and by reviewers. Subscribes rather than fetching once, so an
 * attempt that has just completed (or is still finalizing) fills in without a
 * manual refresh.
 *
 * Attempts are written server-side only and are read-only to the client
 * (firestore.rules). A read failure is surfaced, not thrown, so the rest of the
 * surface never breaks if a role lacks access.
 *
 * @param userId - The applicant whose attempts to subscribe to; when undefined
 *   the hook resets to an empty, non-loading state.
 * @param courseId - The target course the applicant is applying to.
 * @param options - See {@link UseSectionTeachingAttemptsOptions}. Omitted →
 *   the real applicant collection.
 * @return `{attempts, loading, error}` — attempts ordered by `attemptNumber`.
 */
export const useSectionTeachingAttempts = (
  userId: string | undefined,
  courseId: CourseId,
  options?: UseSectionTeachingAttemptsOptions,
) => {
  const [attempts, setAttempts] = useState<SectionTeachingAttempt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // A primitive dependency, so callers may pass an inline options object
  // without resubscribing every render.
  const demo = options?.demo === true;

  useEffect(() => {
    if (!userId) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- reset when there is no applicant to read
      setAttempts([]);
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- reset when there is no applicant to read
      setLoading(false);
      return;
    }
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- show loading while the first snapshot arrives
    setLoading(true);
    const unsubscribe = onSnapshot(
      query(
        getSectionTeachingAttemptsCollectionRef(userId as UserId, courseId, {
          demo,
        }),
        orderBy('attemptNumber', 'asc'),
      ),
      snap => {
        setAttempts(snap.docs.map(d => d.data()));
        setError(null);
        setLoading(false);
      },
      err => {
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [userId, courseId, demo]);

  return {attempts, loading, error};
};
